import React, { useEffect, useRef } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useToast } from '../../contexts/ToastContext';

const UpdateToast: React.FC = () => {
  const { toastVisible, toastMessage, toastActionLabel, toastOnAction, hideToast } = useToast();
  const toastRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only render on web
    if (Platform.OS !== 'web') {
      return;
    }

    if (toastVisible && toastRef.current) {
      // Trigger enter animation
      toastRef.current.classList.add('toast-enter');
      toastRef.current.classList.remove('toast-exit');

      // Auto-dismiss after 10 seconds if no action
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (toastVisible) {
          hideToast();
        }
      }, 10000);
    } else if (!toastVisible && toastRef.current) {
      // Trigger exit animation
      toastRef.current.classList.add('toast-exit');
      toastRef.current.classList.remove('toast-enter');
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [toastVisible, hideToast]);

  const handleAction = () => {
    if (toastOnAction) {
      toastOnAction();
    }
    hideToast();
  };

  // Don't render on non-web platforms
  if (Platform.OS !== 'web' || !toastVisible) {
    return null;
  }

  return (
    <>
      <style>{`
        .update-toast-container {
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%) translateY(100px);
          z-index: 10000;
          max-width: 500px;
          width: calc(100% - 48px);
          pointer-events: none;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
          opacity: 0;
        }

        .update-toast-container.toast-enter {
          transform: translateX(-50%) translateY(0);
          opacity: 1;
          pointer-events: auto;
        }

        .update-toast-container.toast-exit {
          transform: translateX(-50%) translateY(100px);
          opacity: 0;
          pointer-events: none;
        }

        .update-toast {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 12px;
          padding: 16px 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.2);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        @media (prefers-color-scheme: dark) {
          .update-toast {
            background: rgba(30, 30, 30, 0.98);
            border: 1px solid rgba(255, 255, 255, 0.1);
          }
        }

        .update-toast-message {
          flex: 1;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          font-size: 14px;
          line-height: 1.5;
          color: #1a1a1a;
          margin: 0;
        }

        @media (prefers-color-scheme: dark) {
          .update-toast-message {
            color: #f5f5f5;
          }
        }

        .update-toast-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .update-toast-button {
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease, transform 0.1s ease;
          font-family: inherit;
          white-space: nowrap;
        }

        .update-toast-button:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .update-toast-button:active {
          transform: translateY(0);
        }

        .update-toast-close {
          background: transparent;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: background-color 0.2s ease, color 0.2s ease;
          font-size: 18px;
          line-height: 1;
          width: 24px;
          height: 24px;
        }

        .update-toast-close:hover {
          background: rgba(0, 0, 0, 0.05);
          color: #1f2937;
        }

        @media (prefers-color-scheme: dark) {
          .update-toast-close {
            color: #9ca3af;
          }

          .update-toast-close:hover {
            background: rgba(255, 255, 255, 0.1);
            color: #f5f5f5;
          }
        }

        @media (max-width: 640px) {
          .update-toast-container {
            bottom: 16px;
            width: calc(100% - 32px);
          }

          .update-toast {
            padding: 12px 16px;
          }

          .update-toast-message {
            font-size: 13px;
          }

          .update-toast-button {
            padding: 6px 12px;
            font-size: 13px;
          }
        }
      `}</style>
      <div
        ref={toastRef}
        className={`update-toast-container ${toastVisible ? 'toast-enter' : 'toast-exit'}`}
      >
        <div className="update-toast">
          <p className="update-toast-message">{toastMessage}</p>
          <div className="update-toast-actions">
            {toastActionLabel && toastOnAction && (
              <button className="update-toast-button" onClick={handleAction}>
                {toastActionLabel}
              </button>
            )}
            <button className="update-toast-close" onClick={hideToast} aria-label="Close">
              ×
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpdateToast;

